import { useMemo, useRef, useState, useEffect } from 'react';
import { MdOutlineArrowBackIosNew, MdOutlineArrowForwardIos, MdClose, MdMenu, MdPlayArrow, MdPause } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/sr';
import 'dayjs/locale/sr-cyrl';
import IonRangeSlider from 'react-ion-slider';
import 'ion-rangeslider/css/ion.rangeSlider.min.css';
import './ForecastTopBar.css';

// Format a UTC HHMM string (e.g. "1200") at a given UTC date (dayjs or YYYY-MM-DD)
// as the local-timezone HH:mm equivalent. DST-correct via the JS Date object.
const formatLocalTimeFromUtc = (date, time) => {
    if (!date || !time || time.length !== 4) return '';
    const dateStr = typeof date === 'string' ? date : dayjs(date).format('YYYY-MM-DD');
    const utcDate = new Date(`${dateStr}T${time.slice(0, 2)}:${time.slice(2)}:00Z`);
    if (Number.isNaN(utcDate.getTime())) return '';
    const hh = String(utcDate.getHours()).padStart(2, '0');
    const mm = String(utcDate.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
};

// App language → dayjs locale for the weekday label (same map as WindgramPanel).
const DAYJS_LOCALE = { en: 'en', sr: 'sr', src: 'sr-cyrl' };

// Shared ghost-button style: no border, subtle hover wash — the date is the
// hero of this row, controls only surface on intent.
const GHOST_BTN = 'flex justify-center items-center rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-900/10 dark:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent';

const minutesToHHMM = (mins) => {
    const hh = String(Math.floor(mins / 60)).padStart(2, '0');
    const mm = String(mins % 60).padStart(2, '0');
    return `${hh}${mm}`;
};

// Skysight-style range: local 08:00–20:00 expressed in UTC minutes for the
// given date (DST-correct via getTimezoneOffset on that specific date).
const getLocalDaylightRangeUtcMinutes = (date) => {
    const dateStr = date && typeof date !== 'string' ? dayjs(date).format('YYYY-MM-DD') : (date || dayjs().format('YYYY-MM-DD'));
    const noonLocal = new Date(`${dateStr}T12:00:00`);
    const tzOffsetMin = -noonLocal.getTimezoneOffset(); // CEST: +120, EST: -300
    return {
        min: 8 * 60 - tzOffsetMin,
        max: 20 * 60 - tzOffsetMin,
        tzOffsetMin,
    };
};

const ForecastTopBar = ({
    date,
    time,
    onTimeChange,
    onPrevDate,
    onNextDate,
    canPrevDate = true,
    canNextDate = true,
    siteName,
    onClose,
    onClosePanel,
    panelOpen = false,
    onToggleSideMenu,
}) => {
    const { t, i18n } = useTranslation();
    const [isPlaying, setIsPlaying] = useState(false);
    const weekday = date ? dayjs(date).locale(DAYJS_LOCALE[i18n.language] || 'en').format('ddd') : '';

    // UTC range corresponding to local 08:00–20:00 on the current date.
    const { min: minUtcMin, max: maxUtcMin, tzOffsetMin } = useMemo(
        () => getLocalDaylightRangeUtcMinutes(date),
        [date],
    );

    // Values + matching UTC-minute array for the ion slider. Labels are LOCAL
    // time strings ("08:00", "08:30", ..., "20:00"); we map ion's index back
    // to a UTC HHMM string when committing the URL.
    const sliderValues = useMemo(() => {
        const labels = [];
        const utcMins = [];
        for (let h = 8; h <= 20; h++) {
            labels.push(`${String(h).padStart(2, '0')}:00`);
            utcMins.push(h * 60 - tzOffsetMin);
            if (h < 20) {
                labels.push(`${String(h).padStart(2, '0')}:30`);
                utcMins.push(h * 60 + 30 - tzOffsetMin);
            }
        }
        return { labels, utcMins };
    }, [tzOffsetMin]);

    const propMins = useMemo(
        () => (time ? parseInt(time.slice(0, 2), 10) * 60 + parseInt(time.slice(2), 10) : 720),
        [time],
    );
    // Find closest index in our utcMins array for the current URL time.
    const currentIndex = useMemo(() => {
        let best = 0;
        let bestDiff = Infinity;
        for (let i = 0; i < sliderValues.utcMins.length; i++) {
            const d = Math.abs(sliderValues.utcMins[i] - propMins);
            if (d < bestDiff) { bestDiff = d; best = i; }
        }
        return best;
    }, [propMins, sliderValues]);

    // Animation loop: advance time by 30min every 800ms, loop at end.
    // Uses a ref to read the latest `time` without restarting the interval.
    const timeRef = useRef(time);
    useEffect(() => { timeRef.current = time; }, [time]);
    useEffect(() => {
        if (!isPlaying || !onTimeChange) return;
        const id = setInterval(() => {
            const cur = timeRef.current;
            const curMins = cur ? parseInt(cur.slice(0, 2), 10) * 60 + parseInt(cur.slice(2), 10) : 720;
            const next = curMins >= maxUtcMin ? minUtcMin : curMins + 30;
            onTimeChange(minutesToHHMM(next));
        }, 800);
        return () => clearInterval(id);
    }, [isPlaying, onTimeChange, minUtcMin, maxUtcMin]);
    // Auto-pause when feature/panel closes (onTimeChange becomes null)
    useEffect(() => {
        if (!onTimeChange && isPlaying) setIsPlaying(false);
    }, [onTimeChange, isPlaying]);


    return (
        <header className="md:min-h-[57px] flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 bg-gray-200 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
            <button
                type="button"
                aria-label={t('Toggle menu')}
                onClick={onToggleSideMenu}
                className={`xl:hidden w-[34px] h-[34px] ${GHOST_BTN}`}
            >
                <MdMenu className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-0.5 sm:gap-1">
                <button
                    type="button"
                    aria-label={t('Previous day')}
                    onClick={onPrevDate}
                    disabled={!canPrevDate}
                    className={`w-[30px] h-[30px] sm:w-[34px] sm:h-[34px] ${GHOST_BTN}`}
                >
                    <MdOutlineArrowBackIosNew />
                </button>
                <div className="text-slate-900 dark:text-zinc-200 font-bold text-sm sm:text-lg px-0.5 sm:px-1 min-w-[48px] sm:min-w-[120px] text-center whitespace-nowrap">
                    {date && (
                        <>
                            <span className="md:hidden">{dayjs(date).format('DD.MM.')}</span>
                            <span className="hidden md:inline">
                                <span className="font-medium text-slate-500 dark:text-slate-400 mr-1.5">{weekday}</span>
                                {dayjs(date).format('DD.MM.YYYY')}
                            </span>
                        </>
                    )}
                </div>
                <button
                    type="button"
                    aria-label={t('Next day')}
                    onClick={onNextDate}
                    disabled={!canNextDate}
                    className={`w-[30px] h-[30px] sm:w-[34px] sm:h-[34px] ${GHOST_BTN}`}
                >
                    <MdOutlineArrowForwardIos />
                </button>
            </div>

            {/* Desktop: ion.rangeSlider (round skin) with grid_snap, matching
                the slider from the legacy SkewT modal. URL stays UTC; the values
                array shows local time labels and ion's `from` index maps back
                via sliderValues.utcMins. */}
            {onTimeChange && (
                <div className="hidden md:flex items-center gap-3 mx-4 flex-1">
                    <button
                        type="button"
                        aria-label={isPlaying ? t('Pause') : t('Play')}
                        onClick={() => setIsPlaying((p) => !p)}
                        className="w-9 h-9 flex-shrink-0 flex justify-center items-center rounded-full bg-blue-600 hover:bg-blue-500 text-white shadow-md shadow-blue-600/30 transition-colors"
                    >
                        {isPlaying ? <MdPause className="w-5 h-5" /> : <MdPlayArrow className="w-5 h-5 translate-x-px" />}
                    </button>
                    <div className="forecast-topbar-slider flex-1 px-2">
                        <IonRangeSlider
                            // Remount only when the date/tz changes (which changes the values
                            // array). Changing `from` (current index) should update the existing
                            // slider in place — using a key here would leak jQuery DOM nodes.
                            key={sliderValues.utcMins[0]}
                            grid
                            grid_snap
                            values={sliderValues.labels}
                            from={currentIndex}
                            onFinish={(e) => {
                                setIsPlaying(false);
                                const utcMin = sliderValues.utcMins[e.from];
                                if (Number.isFinite(utcMin)) onTimeChange(minutesToHHMM(utcMin));
                            }}
                            skin="round"
                            hide_min_max
                            hide_from_to
                        />
                    </div>
                </div>
            )}

            {/* Mobile: compact stepper + play */}
            {onTimeChange && (
                <div className="md:hidden flex items-center gap-0.5 mx-0.5">
                    <button
                        type="button"
                        aria-label={t('Earlier')}
                        onClick={() => {
                            setIsPlaying(false);
                            const cur = time ? parseInt(time.slice(0,2), 10) * 60 + parseInt(time.slice(2), 10) : 720;
                            const next = Math.max(minUtcMin, cur - 30);
                            onTimeChange(minutesToHHMM(next));
                        }}
                        className={`w-[30px] h-[30px] sm:w-[34px] sm:h-[34px] font-bold ${GHOST_BTN}`}
                    >
                        −
                    </button>
                    <span className="text-sm font-bold text-slate-900 dark:text-zinc-200 min-w-[44px] text-center">
                        {formatLocalTimeFromUtc(date, time) || '12:00'}
                    </span>
                    <button
                        type="button"
                        aria-label={t('Later')}
                        onClick={() => {
                            setIsPlaying(false);
                            const cur = time ? parseInt(time.slice(0,2), 10) * 60 + parseInt(time.slice(2), 10) : 720;
                            const next = Math.min(maxUtcMin, cur + 30);
                            onTimeChange(minutesToHHMM(next));
                        }}
                        className={`w-[30px] h-[30px] sm:w-[34px] sm:h-[34px] font-bold ${GHOST_BTN}`}
                    >
                        +
                    </button>
                    <button
                        type="button"
                        aria-label={isPlaying ? t('Pause') : t('Play')}
                        onClick={() => setIsPlaying((p) => !p)}
                        className="w-[30px] h-[30px] flex justify-center items-center rounded-full bg-blue-600 text-white shadow-md shadow-blue-600/30"
                    >
                        {isPlaying ? <MdPause /> : <MdPlayArrow className="translate-x-px" />}
                    </button>
                </div>
            )}

            {/* When the slider isn't shown (e.g. point feature) there's no
                flex-1 in the middle to push the right group; add a spacer. */}
            {!onTimeChange && <div className="flex-1" />}

            {siteName && (
                <button
                    type="button"
                    onClick={onClose}
                    className="hidden sm:flex items-center text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-900/10 dark:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white transition-colors px-2.5 py-1.5 rounded-lg truncate max-w-[200px]"
                    title={siteName}
                >
                    <span className="truncate">← {siteName}</span>
                </button>
            )}

            {panelOpen && (
                <button
                    type="button"
                    aria-label={t('Close')}
                    onClick={onClosePanel}
                    className={`w-[30px] h-[30px] sm:w-[34px] sm:h-[34px] ml-auto flex-shrink-0 ${GHOST_BTN} hover:!text-red-500`}
                >
                    <MdClose />
                </button>
            )}
        </header>
    );
};

export default ForecastTopBar;
